import { get, has, isEqual } from 'lodash';
import {
  filterWithMinimumLength,
  flattenObjectsArray,
  formatBoolean,
  formatList,
  formatPhoneNumber,
  hasMinimumLength,
  hasPlainObject,
  isEmpty,
  isPlainObject,
  removeDuplicates,
  removeEmpty,
} from '@/utils/resultsHelper';
import { isSplitUrl, joinUrl } from '@/constants/urls';
import {
  checkRequired,
  checkIdentifier,
  checkMaxLength,
  checkMaxNumeric,
  checkMinLength,
  checkMinNumeric,
  checkNumeric,
  checkValidEmail,
  checkValidUrl,
  checkValidUuid,
  translatedError,
} from '@/utils/errors';
import { formatDate } from '@/utils/dateHelper';
import formFieldTypes, {
  editableFormFieldTypes,
  fieldTypeFormats,
  formFieldArrays,
  formFieldSimpleArrays,
  includesFieldType,
} from './constants';
import {
  isFormEmailField,
  isFormMultiplesField,
  isFormUriField,
  isFormUuidField,
  linkFields,
} from './state/state';

export const camelConverter = (str, prefix) => str.replace(/[A-Z]/g, (letter) => `${prefix}${letter.toLowerCase()}`);

const safeTypedValue = (value) => {
  let copy = value;

  if (hasPlainObject(value)) {
    [copy] = value;
  } else if (Array.isArray(value)) {
    copy = { value };
  }

  if (!isPlainObject(copy)) {
    copy = { value: copy };
  }

  const values = removeEmpty(Object.values(copy));
  return values.length > 0 ? values.join(', ') : undefined;
};

export const typedValue = (value, intl, { type, options }) => {
  const copy = safeTypedValue(value);
  try {
    switch (type) {
      case formFieldTypes.DATE.type: return formatDate(copy);
      case formFieldTypes.PHONE.type: return formatPhoneNumber(copy);
      case formFieldTypes.BOOL.type: return formatBoolean(copy, intl);
      case formFieldTypes.LIST.type: return formatList(copy, options);
      default: return isSplitUrl(value) ? joinUrl(value) : copy;
    }
  } catch (err) {
    return copy;
  }
};

export const formFieldPath = ({ parent, field }, prefix = '') => (
  parent ? `${prefix}${parent.name}.${field.name}` : `${prefix}${field.name}`
);

export const formFieldArrayPath = ({ parent, field }, prefix = '', index = 0) => (
  parent ? `${prefix}${parent.name}[${index}].${field.name}` : `${prefix}[${index}].${field.name}`
);

const safeLinkValue = (value) => {
  const link = Array.isArray(value) ? value[0] : value;
  return isPlainObject(link) ? link : { link };
};

export const linkValue = (value, field) => {
  const text = safeLinkValue(value);
  const [link] = filterWithMinimumLength(['link', 'url'].map((name) => (
    isSplitUrl(text[name]) ? joinUrl(text[name]) : text[name]
  )));
  if (isEmpty(link)) { return null; }

  const [display] = filterWithMinimumLength(['text', 'name'].map((name) => text[name]));
  const uri = isFormEmailField(field.link.validation) ? `mailto:${link}` : link;
  return `[${display || link}](${uri})`;
};

const formFieldType = ({ validation, type }) => {
  const { format } = fieldTypeFormats[validation?.format]?.[type] || { format: type };
  return format;
};

const lineError = (errors, values, message) => {
  const [error] = errors;
  const errorValues = [...new Set(errors.map(({ translation }) => translation.messageArgs.value))];
  const lines = errorValues.map((v) => (
    values.reduce((acc, value, idx) => (value === v ? [...acc, idx + 1] : acc), [])
  )).flat().sort((a, b) => a - b);

  error.translation.messageArgs.count = errors.length;
  if (errors.length === 1) {
    error.message = message;
    error.translation.messageArgs.lines = lines;
  } else {
    error.translation.messageArgs.lines = lines.slice(0, 3).join(', ') + (lines.length > 3 ? '...' : '');
  }

  return error;
};

const validateFormatContexts = [
  {
    singular: 'url',
    plural: 'urls',
    check: isFormUriField,
    fn: checkValidUrl,
  },
  {
    singular: 'email',
    plural: 'emails',
    check: isFormEmailField,
    fn: checkValidEmail,
  },
  {
    singular: 'uuid',
    plural: 'uuids',
    check: isFormUuidField,
    fn: checkValidUuid,
  },
];

const validateFormat = (values, names, errors, entity, title, validation, multiples = false) => {
  const context = validateFormatContexts.find(({ check }) => check(validation));
  if (context) {
    const formatErrors = [];
    const singular = `validation.${context.singular}.not.valid`;
    const multiple = `validation.${context.plural}.not.valid`;
    values.forEach((value) => context.fn(value, names, formatErrors, entity, title, multiples ? multiple : singular));

    if (multiples && formatErrors.length > 0) {
      errors.push(lineError(formatErrors, values, `validation.${context.plural}.one.not.valid`));
    } else {
      errors.push(...formatErrors);
    }
  }
};

const arrayToCheck = (value, field) => {
  const items = Array.isArray(value) ? value : [];
  return includesFieldType(formFieldSimpleArrays, field.type) ? removeDuplicates(items, true) : removeEmpty(items);
};

const validateMinMax = (value, field, fields, errors, entity, title, validation) => {
  if (includesFieldType([formFieldTypes.TEXT, formFieldTypes.STRING, formFieldTypes.MD], field.type)) {
    checkIdentifier(value, fields, errors, entity, title, validation.identifier || false);
    checkMinLength(value, fields, errors, entity, title, validation.minimum);
    checkMaxLength(value, fields, errors, entity, title, validation.maximum);
  } else if (includesFieldType([formFieldTypes.NUMBER], field.type)) {
    checkNumeric(value, fields, errors, entity, title);
    checkMinNumeric(value, fields, errors, entity, title, validation.minimum);
    checkMaxNumeric(value, fields, errors, entity, title, validation.maximum);
  }
};

const validateArrayTypes = (value, field, errors, entity, title, names = []) => {
  const valueToCheck = arrayToCheck(value, field);
  const fields = [...names, field.name];
  const validation = field.validation || {};
  const requireCheck = valueToCheck.length === 0 ? null : 1;

  if (validation.required || false) {
    checkRequired(requireCheck, fields, errors, entity, title, true);
  }

  if (requireCheck) {
    validateFormat(valueToCheck, fields, errors, entity, title, validation, true);
    checkMinNumeric(valueToCheck.length, fields, errors, entity, title, validation.minimum);
    checkMaxNumeric(valueToCheck.length, fields, errors, entity, title, validation.maximum);
  }
};

const validateSimpleTypes = (value, field, errors, entity, title, names = []) => {
  const fields = [...names, field.name];
  const validation = field.validation || {};
  checkRequired(value, fields, errors, entity, title, validation.required || false);
  validateFormat([value], fields, errors, entity, title, validation);

  if (value) {
    validateMinMax(value, field, fields, errors, entity, title, validation);
  }
};

const validateArraysAndSimpleTypes = (value, field, errors, entity, title, names = []) => {
  if ((includesFieldType(formFieldArrays, field.type) || isFormMultiplesField(field.validation || {}))) {
    validateArrayTypes(value, field, errors, entity, title, names);
  } else {
    validateSimpleTypes(value, field, errors, entity, title, names);
  }
};

const formObjectError = (form, title, field, entity, errors, parent) => {
  const path = formFieldPath({ parent, field });

  if (has(form, path)) {
    validateArraysAndSimpleTypes(get(form, path), field, errors, entity, title, [parent.name]);
  }
};

const formLinkErrors = (form, title, parent, entity) => {
  const errors = [];

  linkFields(parent).forEach((field) => (
    formObjectError(form, title, field, entity, errors, parent)));

  return errors;
};

const formObjectErrors = (form, title, parent, entity) => {
  const errors = [];

  parent.fields.forEach((field) => (
    formObjectError(form, title, field, entity, errors, parent)));

  return errors;
};

const formFieldError = (form, title, field, entity, errors, path) => {
  if (includesFieldType([formFieldTypes.OBJECT], field.type)) {
    errors.push(...formObjectErrors(form, title, field, entity));
  } else if (includesFieldType([formFieldTypes.LINK], field.type)) {
    errors.push(...formLinkErrors(form, title, field, entity));
  } else {
    validateArraysAndSimpleTypes(get(form, path), field, errors, entity, title);
  }
};

export const formFieldErrors = (form, title, fields, entity) => {
  const errors = [];
  fields.forEach((field) => {
    const path = formFieldPath({ field });
    if (has(form, path)) {
      formFieldError(form, title, field, entity, errors, path);
    }
  });

  return errors;
};

const formArrayObjectDuplicates = (items, message) => {
  const errors = [];
  const flattened = flattenObjectsArray(items);

  flattened.forEach((obj, index) => {
    if (flattened.some((item, i) => i !== index && isEqual(item, obj))) {
      errors.push(translatedError(index.toString(), message, 'validation.general.duplicates'));
    }
  });

  return errors;
};

const validateFormArrayObjects = (field, fields, items, message) => {
  const sanitised = removeEmpty(items);
  const errors = formArrayObjectDuplicates(sanitised, message);

  validateArrayTypes(items, field, errors, 'server', message);
  const fieldErrors = sanitised.map((item, index) => formFieldErrors(item, message, fields, index.toString())).flat();

  return errors.concat(...fieldErrors);
};

export const validateFormObjects = (field, items) => {
  const validator = {
    [formFieldTypes.LINKS.type]: { fields: () => linkFields(field), message: 'form.invalid.links' },
    [formFieldTypes.OBJECTS.type]: { fields: () => field.fields, message: 'form.invalid.objects' },
  }[field.type];

  return validateFormArrayObjects(field, validator.fields(), items, validator.message);
};

const shouldDisplayFormLink = (value) => {
  if (isSplitUrl(value)) {
    return hasMinimumLength(joinUrl(value));
  }
  return hasMinimumLength(value);
};

export const shouldDisplayFormField = (canEdit, component, value, { type }) => {
  if (!component) {
    return false;
  }

  if (canEdit || type === formFieldTypes.LABEL.type) {
    return true;
  } if (type === formFieldTypes.LINK.type) {
    return shouldDisplayFormLink(value?.link);
  } if (isSplitUrl(value)) {
    return hasMinimumLength(joinUrl(value));
  } if (isPlainObject(value)) {
    const hasValues = Object.keys(value).map((key) => (
      (isSplitUrl(value[key]) ? hasMinimumLength(joinUrl(value[key])) : hasMinimumLength(value[key]))));
    return hasValues.some((hasValue) => hasValue === true);
  } if (Array.isArray(value)) {
    return value.length > 0;
  }

  return hasMinimumLength(value);
};

export const formFieldText = (root, field, prefix = '', parent = null) => {
  let text = get(root, formFieldPath({ parent, field }, prefix));
  if (!text && parent) {
    text = get(root, formFieldArrayPath({ parent, field }, prefix));
  }

  return text;
};

export const formDisplayFields = (fields, excludeFields = []) => fields.filter((f) => (
  !excludeFields.includes(f.name) && editableFormFieldTypes.includes(f.type)));

export default formFieldType;
